/**
 * TerrainPins — POI-Marker auf dem Terrain, ENGINE-NATIV.
 *
 * Architektur-Korrektur (Alex 2026-08-15: „wir verwenden dieselbe Engine,
 * aber du baust alles selber nach"): Jeder Marker ist ein echtes Engine-POI
 * (POI + POIData + ShapeFactory) — TileShape fuer runde Platten,
 * QuaderShape fuer Cubes. Damit kommen Material-Pipeline, Billboard,
 * Texturen und Pickbarkeit aus der Klassenhierarchie statt aus
 * nachgebauten THREE-Meshes. Dieses Modul steuert nur noch:
 *  – Datenbeschaffung (Track-30 + Kategorie-Glyphen, same-origin)
 *  – Renderer-Zuordnung je POI-Typ (Vertrag Content-Post #4702)
 *  – Force-Simulation der runden Platten (Weltkoordinaten, Terrain-Boden)
 *  – statische Cube-Stacks als starre Force-Collider
 *
 * Vertrag #4702 (3DPresenter-Tschepp, verbindlich):
 *  – amenity/transport → Cube (auch solo); Stack ab >=2 im complete-link-
 *    Radius 0.0065u (32,5 m); Reihenfolge unten→oben: parking, bus_stop,
 *    restaurant/kiosk, kassa/ticket, helipad/firstaid, sonstige; Tie → ID.
 *  – Cube 0.0049u, Luft 12 % der Kante, Mast bis Unterkante 0.0106u,
 *    Mastradius 0.00009u; RoundedBox corner 8 %, bevel 2.5 %; WELTFEST
 *    (kein Billboard); Glyph contain, nie cover; role=main nie auf Flaechen.
 *  – navigation/decision → gelbes Weggabelungszeichen, eigener Anker.
 *  – nature/Species/Sehenswuerdigkeiten → runde Platte im Force-System.
 *  – Stack = starrer statischer Compound (inverseMass 0); runde Pins
 *    weichen dem Stack-AABB aus. Jeder Cube einzeln pickbar (eigenes POI).
 */
import * as THREE from 'three';
import { SceneNode } from '../engine/core/SceneNode';
import { POI } from '../engine/poi/POI';
import { POIData } from '../engine/poi/POIData';
import { Color, Vector3, TextRenderMode } from '../engine/types';
import type { SceneSettingsData } from '../engine/core/SceneSettingsData';
import type { AppSettingsData } from '../engine/core/AppSettingsData';
import type { LayoutData } from '../engine/layout/LayoutData';
import type { TerrainRoot } from '../engine/terrain/TerrainRoot';
import { MapCategoryPinNode } from '../engine/map/markers/MapCategoryPinNode';
import { MapDecisionPinNode } from '../engine/map/markers/MapDecisionPinNode';
import { MapPoiStackPinNode, gorgeStackRank, isGorgeStackPin } from '../engine/map/markers/MapPoiStackPinNode';
import { classifyMapMarker } from '../engine/map/markers/MapMarkerClassifier';
import { resolveMapMarkerMetrics, type MapMarkerMetricsWorld } from '../engine/map/markers/MapMarkerMetrics';
import { buildCategoryStackLayout, buildExplicitStackLayout } from '../engine/map/markers/MapStackLayout';
import type { IMapSurface } from '../engine/map/MapSurface';
import type { BridgePin } from '../embed/MapBridge';

interface PoiDaten {
    id: number;
    kind?: BridgePin['kind'];
    lat: number;
    lng: number;
    titel: string;
    kategorie: string | null;
    unterkategorie: string | null;
    istFoto: boolean;
    prioritaet: number;
    farbe?: string | null;
    ikone: string | null;
    bild: string | null;
}

interface RunderPin {
    daten: PoiDaten;
    poi: POI;
    mast: THREE.Mesh;
    ankerX: number;
    ankerZ: number;
    bodenY: number | null;
    pos: THREE.Vector3;
    vel: THREE.Vector3;
}

interface CubeStack {
    /** DIE Klassen aus ?app=map — identischer Code, identische Optik */
    members: { daten: PoiDaten; node: MapCategoryPinNode | MapPoiStackPinNode; hoeheUeberGrund: number }[];
    x: number;
    z: number;
    hoehe: number;
    posiert: boolean;
    /** Welt-AABB als starrer Force-Collider fuer runde Pins */
    aabb: THREE.Box3;
}

interface RautenZeichen {
    daten: PoiDaten;
    node: MapDecisionPinNode;
    x: number;
    z: number;
    posiert: boolean;
}

const FLORA_FARBE = '#22C55E', FAUNA_FARBE = '#15803D';
const MAST_FARBE = '#B8C0C8';
export interface TerrainPinsConfig {
    terrain: TerrainRoot;
    surface: IMapSurface;
    sceneSettings: SceneSettingsData;
    appSettings: AppSettingsData;
    layoutData?: LayoutData;
    datenUrl?: string;
    glyphenUrl?: string;
    /** Plattenradius in Welt-Units (0.004 = 20 m bei 1u=5km) */
    plattenRadius?: number;
    /** Ruhehoehe der Platte ueber Grund */
    nabe?: number;
    /** Simulations-/Sichtradius um die Kamera in Welt-Units */
    aktivRadius?: number;
    /** Standalone demo data. Embed renderers receive pins through MapBridge. */
    autoLoad?: boolean;
}

export class TerrainPins extends SceneNode {
    private readonly _cfg: Required<Omit<TerrainPinsConfig, 'layoutData'>> & { layoutData?: LayoutData };
    private readonly _terrain: TerrainRoot;
    private readonly _surface: IMapSurface;
    private readonly _metrics: MapMarkerMetricsWorld;
    private _pins: RunderPin[] = [];
    private _stacks: CubeStack[] = [];
    private _kamera: THREE.Camera | null = null;
    private _glyphen: any = null;
    private _kategorien: Record<string, { farbe: string }> = {};
    private _mastGeo: THREE.CylinderGeometry;
    private _mastMaterial: THREE.MeshStandardMaterial;
    private _tmp = new THREE.Vector3();
    private _tmpKam = new THREE.Vector3();
    private _posPuffer = new Vector3(0, 0, 0);
    private _filter: Set<string> | null = null;
    private _aktiv = true;
    private _activePinId: number | null = null;
    private _glyphLoad: Promise<void> | null = null;
    private readonly _raycaster = new THREE.Raycaster();
    private readonly _pointer = new THREE.Vector2();
    private _pendingPinPositions = new Map<number, THREE.Vector3>();
    private _dataGeneration = 0;

    constructor(cfg: TerrainPinsConfig) {
        super('TerrainPins');
        const metrics = resolveMapMarkerMetrics(cfg.surface);
        this._cfg = {
            datenUrl: 'https://kaernten.arkserver.arkturian.com/3d/daten.json',
            glyphenUrl: 'https://kaernten.arkserver.arkturian.com/3d/category-glyphs.json',
            plattenRadius: metrics.iconSignSize * 0.5,
            nabe: metrics.iconPoleHeight + metrics.iconSignSize * 0.5,
            aktivRadius: 5,
            autoLoad: true,
            ...cfg,
        };
        this._terrain = cfg.terrain;
        this._surface = cfg.surface;
        this._metrics = metrics;
        this._mesh = new THREE.Group();
        this._mesh.name = 'TerrainPins';
        this._mesh.userData.sceneNode = this;

        // Silbermast nach Vertrag: konstante Staerke, kein Taper
        this._mastGeo = new THREE.CylinderGeometry(metrics.iconSignSize * 0.018, metrics.iconSignSize * 0.018, 1, 8);
        this._mastGeo.translate(0, 0.5, 0);
        this._mastMaterial = new THREE.MeshStandardMaterial({
            color: MAST_FARBE, metalness: 0.76, roughness: 0.32,
        });

        if (this._cfg.autoLoad) void this._lade().catch((e) => console.warn("[terrain] Standalone-Daten nicht ladbar (Embed unbeeinflusst):", e));
    }

    setCamera(kamera: THREE.Camera): void {
        this._kamera = kamera;
    }

    /** Full-replace pin feed used by MapBridge v0.3. */
    async setBridgePins(pins: BridgePin[]): Promise<void> {
        const generation = ++this._dataGeneration;
        const pendingPinPositions = new Map<number, THREE.Vector3>();
        for (const pin of pins) {
            if (pin.lat === undefined || pin.lng === undefined) continue;
            const world = this._surface.geoToWorld(pin.lat, pin.lng);
            if (world) pendingPinPositions.set(pin.id, world);
        }
        await this._loadGlyphs();
        if (generation !== this._dataGeneration) return;
        const daten: PoiDaten[] = pins
            .filter(pin => pin.lat !== undefined && pin.lng !== undefined)
            .map(pin => ({
                id: pin.id,
                kind: pin.kind,
                lat: pin.lat!,
                lng: pin.lng!,
                titel: pin.title,
                kategorie: pin.category ?? null,
                unterkategorie: pin.subcategory ?? null,
                istFoto: false,
                prioritaet: 0,
                farbe: pin.color ?? null,
                ikone: pin.iconUrl ?? null,
                bild: pin.kind === 'species' ? pin.thumbnailUrl ?? null : null,
            }));
        this._setData(daten);
        // _setData intentionally clears all old marker state. Keep the fresh
        // geo anchors afterwards as a focus fallback for pins whose visual
        // strategy may collapse or omit an individual marker.
        this._pendingPinPositions = pendingPinPositions;
    }

    setCategoryFilter(categories: string[] | null): void {
        this._filter = categories ? new Set(categories) : null;
    }

    /** Semantic selection only — never moves the camera or emits a tap. */
    setActivePin(id: number | null): void {
        this._activePinId = id;
        this._applyActiveVisuals();
    }

    refreshSurface(): void {
        for (const pin of this._pins) pin.bodenY = null;
        for (const stack of this._stacks) stack.posiert = false;
        for (const decision of this._rauten) decision.posiert = false;
    }

    getPinWorldPosition(id: number): THREE.Vector3 | null {
        const round = this._pins.find(pin => pin.daten.id === id);
        if (round) return round.pos.clone();
        for (const stack of this._stacks) {
            const member = stack.members.find(item => item.daten.id === id);
            if (member) {
                if (!stack.posiert) {
                    return new THREE.Vector3(
                        stack.x,
                        this._surface.getWorldY(stack.x, stack.z) ?? 0,
                        stack.z,
                    );
                }
                const position = member.node.transform.position.value;
                return position ? new THREE.Vector3(position.x, position.y, position.z) : null;
            }
        }
        const decision = this._rauten.find(item => item.daten.id === id);
        if (decision) {
            if (!decision.posiert) {
                return new THREE.Vector3(
                    decision.x,
                    this._surface.getWorldY(decision.x, decision.z) ?? 0,
                    decision.z,
                );
            }
            const position = decision.node.transform.position.value;
            return position ? new THREE.Vector3(position.x, position.y, position.z) : null;
        }
        const pending = this._pendingPinPositions.get(id);
        if (!pending) return null;
        const world = pending.clone();
        world.y = this._surface.getWorldY(world.x, world.z) ?? world.y;
        return world;
    }

    pick(clientX: number, clientY: number, camera: THREE.Camera, canvas: HTMLCanvasElement): number | null {
        const rect = canvas.getBoundingClientRect();
        this._pointer.set(
            ((clientX - rect.left) / rect.width) * 2 - 1,
            -((clientY - rect.top) / rect.height) * 2 + 1,
        );
        this._raycaster.setFromCamera(this._pointer, camera);
        const root = this.getThreeObjects()[0];
        if (!root) return null;
        for (const hit of this._raycaster.intersectObject(root, true)) {
            let object: THREE.Object3D | null = hit.object;
            while (object && object !== root.parent) {
                const id = object.userData.bridgePinId;
                if (typeof id === 'number') return id;
                object = object.parent;
            }
        }
        return null;
    }

    protected calculateBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        if (this._mesh) box.setFromObject(this._mesh);
        if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(1, 1, 1));
        return box;
    }

    // ── Daten + Renderer-Zuordnung (#4702: exklusiv je POI) ─────────────

    private async _lade(): Promise<void> {
        const generation = ++this._dataGeneration;
        const [daten] = await Promise.all([
            fetch(this._cfg.datenUrl).then((r) => r.json()),
            this._loadGlyphs(),
        ]);
        if (generation !== this._dataGeneration) return;
        this._kategorien = daten.kategorien ?? {};
        this._setData((daten.pois as PoiDaten[]).filter((p) => p.titel && !p.istFoto));
    }

    private _loadGlyphs(): Promise<void> {
        if (!this._glyphLoad) {
            this._glyphLoad = fetch(this._cfg.glyphenUrl)
                .then(response => response.json())
                .then(glyphs => { this._glyphen = glyphs; })
                .catch(() => { this._glyphen = null; });
        }
        return this._glyphLoad;
    }

    private _setData(alle: PoiDaten[]): void {
        this._clearMarkers();
        const classified = alle.map(daten => ({ daten, kind: classifyMapMarker(this._alsBridgePin(daten)) }));
        const cubes = classified.filter(item => item.kind === 'category-cube').map(item => item.daten);
        const gorge = classified.filter(item => item.kind === 'stack').map(item => item.daten);
        const rauten = classified.filter(item => item.kind === 'decision').map(item => item.daten);
        const platten = classified
            .filter(item => item.kind !== 'category-cube' && item.kind !== 'stack' && item.kind !== 'decision')
            .map(item => item.daten);

        this._baueStacks(cubes);
        this._baueGorgeStack(gorge);
        this._baueRauten(rauten);
        for (const p of platten) this._bauePin(p, this._plattenPoi(p));
        this._applyActiveVisuals();
    }

    private _bauePin(daten: PoiDaten, poi: POI): void {
        const welt = this._terrain.projection.geoToWorld(daten.lng, daten.lat, 0);
        const mast = new THREE.Mesh(this._mastGeo, this._mastMaterial);
        mast.scale.y = 0.0001;
        mast.visible = false;
        poi.visible = false;
        if (poi.getThreeObjects()[0]) poi.getThreeObjects()[0].visible = false;
        this.addChild(poi);
        const object = poi.getThreeObjects()[0];
        if (object) object.userData.bridgePinId = daten.id;
        mast.userData.bridgePinId = daten.id;
        (this._mesh as THREE.Group).add(mast);
        this._pins.push({
            daten, poi, mast,
            ankerX: welt.x, ankerZ: welt.z, bodenY: null,
            pos: new THREE.Vector3(welt.x, 0, welt.z),
            vel: new THREE.Vector3(),
        });
    }

    /** Runde Platte: Engine-TileShape (Kreis via cornerRadius 0.5), Foto/Glyph. */
    private _plattenPoi(p: PoiDaten): POI {
        const d = this._basisDaten(p);
        d.shape.value = 'tile';
        d.cornerRadius.value = 0.5;
        d.edgeRadius.value = 0.035;
        d.depth.value = 0.14;
        d.enableBillboard.value = true;
        d.scale.value = this._cfg.plattenRadius * 2;
        const foto = p.ikone ?? p.bild;
        d.uri.value = [foto ?? this._glyphDataUrl(p, this._farbe(p), false)];
        return new POI(`terrainpin-${p.id}`, d, this._cfg.sceneSettings, this._cfg.appSettings, this._cfg.layoutData);
    }

    /** BridgePin-Sicht auf unsere POI-Daten — fuer die ?app=map-Klassen. */
    private _alsBridgePin(p: PoiDaten): BridgePin {
        return {
            id: p.id, kind: p.kind ?? 'poi', title: p.titel,
            lat: p.lat, lng: p.lng,
            category: p.kategorie ?? undefined,
            subcategory: p.unterkategorie ?? undefined,
            color: p.farbe ?? undefined,
            iconUrl: p.ikone ?? undefined,
        };
    }

    /**
     * Cube-Stacks: EXAKT die Klassen und das Layout aus ?app=map
     * (MapCategoryPinNode + buildCategoryPinLayout-Verfahren), nur mit den
     * Vertrag-#4702-Massen fuer den Terrain-Koordinatenraum.
     */
    private _baueStacks(pois: PoiDaten[]): void {
        const bridgePins = pois.map(p => this._alsBridgePin(p));
        const resolved = this._resolvePins(bridgePins);
        const layout = buildCategoryStackLayout(bridgePins, resolved, this._surface, this._metrics);
        const grouped = new Map<string, typeof pois>();
        for (const p of pois) {
            const placement = layout.get(p.id);
            if (!placement) continue;
            const key = `${placement.local.x.toFixed(8)}/${placement.local.z.toFixed(8)}`;
            const group = grouped.get(key) ?? [];
            group.push(p);
            grouped.set(key, group);
        }
        for (const group of grouped.values()) {
            const first = layout.get(group[0].id)!;
            const x = first.local.x, z = first.local.z;
            let maxTop = 0;
            const members = group.map((p) => {
                const bp = this._alsBridgePin(p);
                const placement = layout.get(p.id)!;
                const node = new MapCategoryPinNode(bp, this._metrics.categoryCubeSize, placement.poleHeight);
                const ground = this._surface.getWorldY(x, z) ?? 0;
                const hoeheUeberGrund = placement.local.y - ground;
                maxTop = Math.max(maxTop, hoeheUeberGrund + this._metrics.categoryCubeSize * 0.5);
                node.visible = false;
                const obj = node.getThreeObjects()[0];
                if (obj) obj.visible = false;
                this.addChild(node);
                return { daten: p, node, hoeheUeberGrund };
            });
            this._stacks.push({
                members, x, z, hoehe: maxTop, posiert: false, aabb: new THREE.Box3(),
            });
        }
    }

    private _baueGorgeStack(pois: PoiDaten[]): void {
        const bridgePins = pois.map(p => this._alsBridgePin(p));
        const resolved = this._resolvePins(bridgePins);
        const layout = buildExplicitStackLayout(bridgePins, resolved, this._surface, {
            includes: isGorgeStackPin,
            rank: gorgeStackRank,
            cubeSize: this._metrics.gorgeCubeSize,
            poleHeight: this._metrics.categoryPoleHeight,
            gapFactor: this._metrics.stackGapFactor,
        });
        if (layout.size === 0) return;
        const first = layout.values().next().value;
        if (!first) return;
        const x = first.local.x, z = first.local.z;
        let maxTop = 0;
        const members = pois
            .filter(p => layout.has(p.id))
            .sort((a, b) => gorgeStackRank(this._alsBridgePin(a)) - gorgeStackRank(this._alsBridgePin(b)))
            .map(p => {
                const bp = this._alsBridgePin(p);
                const placement = layout.get(p.id)!;
                const node = new MapPoiStackPinNode(bp, this._metrics.gorgeCubeSize, placement.poleHeight, bp.iconUrl);
                const ground = this._surface.getWorldY(x, z) ?? 0;
                const hoeheUeberGrund = placement.local.y - ground;
                maxTop = Math.max(maxTop, hoeheUeberGrund + this._metrics.gorgeCubeSize * 0.5);
                node.visible = false;
                const obj = node.getThreeObjects()[0];
                if (obj) obj.visible = false;
                this.addChild(node);
                return { daten: p, node, hoeheUeberGrund };
            });
        this._stacks.push({ members, x, z, hoehe: maxTop, posiert: false, aabb: new THREE.Box3() });
    }

    private _resolvePins(pins: BridgePin[]): Map<number, THREE.Vector3> {
        const resolved = new Map<number, THREE.Vector3>();
        for (const pin of pins) {
            if (pin.lat === undefined || pin.lng === undefined) continue;
            const world = this._surface.geoToWorld(pin.lat, pin.lng);
            if (world) resolved.set(pin.id, world);
        }
        return resolved;
    }

    /** Gelbe Weggabelungszeichen: MapDecisionPinNode aus ?app=map, statisch
     * am eigenen GPS-Anker (#4702). Skaliert mit demselben Faktor wie die
     * Cubes (0.0049/0.024): Zeichen 0.0078, Mast 0.0088. */
    private _rauten: RautenZeichen[] = [];
    private _baueRauten(pois: PoiDaten[]): void {
        const proj = this._terrain.projection;
        for (const p of pois) {
            const w = proj.geoToWorld(p.lng, p.lat, 0);
            const node = new MapDecisionPinNode(
                this._alsBridgePin(p),
                this._metrics.decisionSignSize,
                this._metrics.decisionPoleHeight,
            );
            node.visible = false;
            const obj = node.getThreeObjects()[0];
            if (obj) obj.visible = false;
            this.addChild(node);
            this._rauten.push({ daten: p, node, x: w.x, z: w.z, posiert: false });
        }
    }

    private _basisDaten(p: PoiDaten): POIData {
        const d = new POIData(this._cfg.sceneSettings, this._cfg.appSettings);
        d.showLabel.value = false;
        d.showConnector.value = false;
        d.labelRenderMode.value = TextRenderMode.SCREEN_HTML_STATIC;
        d.color.value = new Color(this._farbe(p));
        d.localTranslationOffset.value = new Vector3(0, 0, 0);
        return d;
    }

    private _farbe(p: PoiDaten): string {
        if (p.kategorie === 'nature') {
            return p.unterkategorie === 'animal' ? FAUNA_FARBE : FLORA_FARBE;
        }
        return p.farbe ?? this._kategorien[p.kategorie ?? '']?.farbe ?? this._kategorien.poi?.farbe ?? '#F59E0B';
    }

    /** Kategorie-Glyph als Data-URL-Textur (contain + Innenabstand, #4702). */
    private _glyphCache = new Map<string, string>();
    private _glyphDataUrl(p: PoiDaten, farbe: string, dunklerKern: boolean): string {
        const schluessel = `${p.kategorie}/${p.unterkategorie}/${farbe}`;
        const vorhanden = this._glyphCache.get(schluessel);
        if (vorhanden) return vorhanden;
        const c = document.createElement('canvas');
        c.width = c.height = 256;
        const g = c.getContext('2d')!;
        g.fillStyle = farbe;
        g.fillRect(0, 0, 256, 256);
        if (dunklerKern) {
            g.fillStyle = '#102840';
            g.beginPath();
            g.moveTo(128, 30); g.lineTo(226, 128); g.lineTo(128, 226); g.lineTo(30, 128);
            g.closePath(); g.fill();
        }
        // contain mit Innenabstand: 24er-Glyph auf 160 px in 256er-Flaeche
        g.translate(48, 48);
        g.scale(6.6, 6.6);
        g.strokeStyle = '#ffffff';
        g.lineWidth = this._glyphen?.stroke?.strokeWidth ?? 2;
        g.lineCap = 'round';
        g.lineJoin = 'round';
        const G = this._glyphen;
        const frag = G?.glyphs?.[p.unterkategorie ?? '']
            ?? G?.glyphs?.[G?.byCategory?.[p.kategorie ?? ''] ?? '']
            ?? G?.glyphs?.[p.kategorie ?? '']
            ?? G?.glyphs?.[G?.fallback ?? 'map_pin'] ?? '';
        for (const m of String(frag).matchAll(/<path d="([^"]+)"\s*\/>/g)) g.stroke(new Path2D(m[1]));
        for (const m of String(frag).matchAll(/<circle cx="([\d.]+)" cy="([\d.]+)" r="([\d.]+)"\s*\/>/g)) {
            g.beginPath(); g.arc(+m[1], +m[2], +m[3], 0, 7); g.stroke();
        }
        const url = c.toDataURL('image/png');
        this._glyphCache.set(schluessel, url);
        return url;
    }

    // ── Simulation: Force-Platten + statische Stack-Collider ────────────

    /** Track-LOD: schlafen legen — Gruppe unsichtbar, Force-Sim pausiert.
     *  Weit draussen sind die Pins Subpixel; Sim + Draw-Calls waeren reine
     *  Verschwendung (Alex 2026-08-16, Track-LOD-Konzept). */
    setAktiv(aktiv: boolean): void {
        this._aktiv = aktiv;
        if (this._mesh) this._mesh.visible = aktiv;
    }

    protected onUpdate(deltaTime: number): void {
        if (!this._aktiv) return;
        if (!this._kamera) return;
        const dt = Math.min(deltaTime, 0.05);
        const kamPos = this._kamera.getWorldPosition(this._tmpKam);
        const cfg = this._cfg;
        const aktivQuadrat = cfg.aktivRadius * cfg.aktivRadius;

        // Stacks + Rauten: einmal posieren (inverseMass 0), dann nur Sichtbarkeit
        const KANTE = this._metrics.categoryCubeSize;
        for (const s of this._stacks) {
            const dx = s.x - kamPos.x, dz = s.z - kamPos.z;
            const nah = dx * dx + dz * dz < aktivQuadrat;
            if (nah && !s.posiert) {
                const boden = this._terrain.getWorldY(s.x, s.z);
                if (boden !== null) {
                    for (const m of s.members) {
                        this._posPuffer = new Vector3(s.x, boden + m.hoeheUeberGrund, s.z);
                        m.node.transform.position.setImmediate(this._posPuffer);
                    }
                    s.aabb.min.set(s.x - KANTE * 0.7, boden, s.z - KANTE * 0.7);
                    s.aabb.max.set(s.x + KANTE * 0.7, boden + s.hoehe + KANTE, s.z + KANTE * 0.7);
                    s.posiert = true;
                }
            }
            const sichtbar = nah && s.posiert;
            for (const m of s.members) {
                const filtered = !this._isVisibleByFilter(m.daten);
                m.node.visible = sichtbar && !filtered;
                const obj = m.node.getThreeObjects()[0];
                if (obj) obj.visible = sichtbar && !filtered;
            }
        }
        for (const r of this._rauten) {
            const dx = r.x - kamPos.x, dz = r.z - kamPos.z;
            const nah = dx * dx + dz * dz < aktivQuadrat;
            if (nah && !r.posiert) {
                const boden = this._terrain.getWorldY(r.x, r.z);
                if (boden !== null) {
                    this._posPuffer = new Vector3(
                        r.x,
                        boden + this._metrics.decisionPoleHeight + this._metrics.decisionSignSize * 0.5,
                        r.z,
                    );
                    r.node.transform.position.setImmediate(this._posPuffer);
                    r.posiert = true;
                }
            }
            const sichtbar = nah && r.posiert && this._isVisibleByFilter(r.daten);
            r.node.visible = sichtbar;
            const obj = r.node.getThreeObjects()[0];
            if (obj) obj.visible = sichtbar;
        }

        // Runde Platten: Feder + Abstossung + Stack-AABB-Ausweichen + Boden
        const aktive: RunderPin[] = [];
        for (const pin of this._pins) {
            const dx = pin.ankerX - kamPos.x, dz = pin.ankerZ - kamPos.z;
            const nah = dx * dx + dz * dz < aktivQuadrat;
            if (nah) {
                const boden = this._terrain.getWorldY(pin.ankerX, pin.ankerZ);
                if (boden !== null) {
                    if (pin.bodenY === null) pin.pos.set(pin.ankerX, boden + cfg.nabe, pin.ankerZ);
                    pin.bodenY = boden;
                }
            }
            const sichtbar = nah && pin.bodenY !== null && this._isVisibleByFilter(pin.daten);
            if (!sichtbar) {
                pin.poi.visible = false;
                const obj = pin.poi.getThreeObjects()[0];
                if (obj) obj.visible = false;
                pin.mast.visible = false;
                continue;
            }
            aktive.push(pin);
        }

        const feder = 14, daempfung = 0.86, stoss = 8;
        const minAbstand = cfg.plattenRadius * 2.6;
        for (let i = 0; i < aktive.length; i++) {
            const a = aktive[i];
            a.vel.x += (a.ankerX - a.pos.x) * feder * dt;
            a.vel.y += ((a.bodenY ?? 0) + cfg.nabe - a.pos.y) * feder * dt;
            a.vel.z += (a.ankerZ - a.pos.z) * feder * dt;
            for (let j = i + 1; j < aktive.length; j++) {
                const b = aktive[j];
                const dx = b.pos.x - a.pos.x, dy = b.pos.y - a.pos.y, dz = b.pos.z - a.pos.z;
                const dist = Math.hypot(dx, dy, dz);
                if (dist > minAbstand || dist === 0) continue;
                const kraft = ((minAbstand - dist) / minAbstand) * stoss * dt;
                const nx = dx / dist, ny = dy / dist, nz = dz / dist;
                a.vel.x -= nx * kraft; a.vel.y -= ny * kraft; a.vel.z -= nz * kraft;
                b.vel.x += nx * kraft; b.vel.y += ny * kraft; b.vel.z += nz * kraft;
                b.vel.y += kraft * 0.2;
            }
            // Stack-AABBs sind starr: horizontal hinausdraengen (#4702)
            for (const s of this._stacks) {
                if (!s.posiert) continue;
                if (!s.aabb.containsPoint(a.pos)) continue;
                const ax = a.pos.x - s.x, az = a.pos.z - s.z;
                const l = Math.hypot(ax, az) || 0.0001;
                a.vel.x += (ax / l) * stoss * dt * 2;
                a.vel.z += (az / l) * stoss * dt * 2;
            }
            a.vel.multiplyScalar(Math.pow(daempfung, dt * 60));
            a.pos.addScaledVector(a.vel, dt);

            const boden = this._terrain.getWorldY(a.pos.x, a.pos.z) ?? a.bodenY ?? 0;
            const minY = boden + cfg.plattenRadius * 1.6;
            if (a.pos.y < minY) { a.pos.y = minY; if (a.vel.y < 0) a.vel.y = 0; }

            // Pose via Engine-Transform (POI ist der Renderer)
            this._posPuffer = new Vector3(a.pos.x, a.pos.y, a.pos.z);
            a.poi.transform.position.setImmediate(this._posPuffer);

            const fussY = a.bodenY ?? boden;
            a.mast.position.set(a.ankerX, fussY, a.ankerZ);
            const laenge = Math.hypot(a.pos.x - a.ankerX, a.pos.y - fussY, a.pos.z - a.ankerZ);
            a.mast.scale.y = Math.max(0.0001, laenge);
            this._tmp.set(a.pos.x - a.ankerX, a.pos.y - fussY, a.pos.z - a.ankerZ).normalize();
            a.mast.quaternion.setFromUnitVectors(TerrainPins._Y, this._tmp);

            const kamNah = a.pos.distanceToSquared(kamPos) < (cfg.plattenRadius * 5) ** 2;
            a.poi.visible = !kamNah;
            const obj = a.poi.getThreeObjects()[0];
            if (obj) obj.visible = !kamNah;
            a.mast.visible = true;
        }
    }

    private static readonly _Y = new THREE.Vector3(0, 1, 0);

    private _isVisibleByFilter(data: PoiDaten): boolean {
        return data.id === this._activePinId
            || this._filter === null
            || this._filter.has(data.kategorie ?? '');
    }

    private _applyActiveVisuals(): void {
        const apply = (id: number, object: THREE.Object3D | undefined): void => {
            if (!object) return;
            object.scale.setScalar(id === this._activePinId ? 1.2 : 1);
            object.userData.mapActive = id === this._activePinId;
        };
        for (const pin of this._pins) apply(pin.daten.id, pin.poi.getThreeObjects()[0]);
        for (const stack of this._stacks) {
            for (const member of stack.members) apply(member.daten.id, member.node.getThreeObjects()[0]);
        }
        for (const decision of this._rauten) apply(decision.daten.id, decision.node.getThreeObjects()[0]);
    }

    private _clearMarkers(): void {
        for (const child of this.getChildren()) child.destroy();
        for (const pin of this._pins) pin.mast.removeFromParent();
        this._pins = [];
        this._stacks = [];
        this._rauten = [];
        this._pendingPinPositions.clear();
    }

    protected override onDestroy(): void {
        this._clearMarkers();
        this._mastGeo.dispose();
        this._mastMaterial.dispose();
        this._mesh?.removeFromParent();
        this._mesh = null;
        super.onDestroy();
    }
}
